import React, {useState} from 'react';
import {Input} from '~/components/Controls/Input';
import selectStyle from '~/components/Controls/Select/index.module.scss';
import {DropDown} from '~/components/DropDown';
import {QueryList} from '~/components/QueryList';
import {useAbility} from '~/lib/ability';
import {useAllUsersQuery, useImpersonatableUsersQuery} from '~/modules/RillAnalytics/queries';
import {useSession} from '~/modules/Profile/session';
import {RootAbility} from '~/root_ability';
import style from './index.module.scss';

interface IImpersonatableUser {
    id: number;
    email: string;
    name: string;
}

export const UserSwitcher = () => {
    const ability = useAbility(RootAbility);
    const {session, setSelectedImpersonatedUser} = useSession();
    const {data: impersonatable = [], isPending: isImpersonatablePending} = useImpersonatableUsersQuery();
    const {data: allUsers = [], isPending: isUsersPending} = useAllUsersQuery();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    if (!ability.isAdmin()) return null;

    const namesById = new Map(allUsers.map(u => [u.id, u.name]));
    const users: IImpersonatableUser[] = impersonatable.map(u => ({...u, name: namesById.get(u.id) || u.email}));

    const q = query.toLocaleLowerCase();
    const filteredUsers = users.filter(u =>
        String(u.id).includes(q) || u.name.toLocaleLowerCase().includes(q) || u.email.toLocaleLowerCase().includes(q)
    );

    const selected = users.find(u => u.id === session.selectedImpersonatedUserId);

    const select = (userId: number | null) => {
        setQuery('');
        setOpen(false);
        setSelectedImpersonatedUser?.(userId);
    };

    return (
        <DropDown
            className={style.agencySwitcher}
            disabled={isImpersonatablePending || isUsersPending}
            isOpen={open}
            onToggle={setOpen}
            content={() => (
                <>
                    <div className={selectStyle.selectQuery}>
                        <Input value={query} onChange={setQuery} rightIcon={<i className="fa fa-search" />} autoFocus />
                    </div>
                    <div className={selectStyle.selectItems}>
                        <QueryList
                            items={filteredUsers}
                            onSelect={(u) => select(u.id)}
                            emptyRenderer={<div className={selectStyle.selectEmpty}>Empty</div>}
                            itemRenderer={({onClick, item, index}) => (
                                <div onClick={onClick} className={selectStyle.selectItem} key={index}>
                                    #{item.id} — {item.name}
                                </div>
                            )}
                        />
                    </div>
                </>
            )}
        >
            <div className={selectStyle.selectCurrentItem}>
                {selected ? (
                    <>
                        #{selected.id} — {selected.name}
                        <i className={['fa fa-close', selectStyle.selectClear].join(' ')} onClick={(event) => {
                            event.stopPropagation();
                            select(null);
                        }} />
                    </>
                ) : (
                    <span className={selectStyle.selectPlaceholder}>View as...</span>
                )}
            </div>
        </DropDown>
    );
};
